import { getApiUrl, getApiAuthInfo } from './config';
import { isElectron } from '@/utils/electron';
import { storage } from '@/utils/storageOperateApi';
import { LOCAL_STORAGE_KEY } from '@/constants';
import { PORTVERSION } from '../index';
import { isOverseasEnv } from '@/utils/api';
import { getOverseaHeader } from '@/utils/oversea';
function toHeadersObject(extra) {
  if (!extra) return {};
  if (extra instanceof Headers) {
    const obj = {};
    extra.forEach((v, k) => (obj[k] = v));
    return obj;
  }
  if (typeof extra === 'object' && !Array.isArray(extra)) return { ...extra };
  return {};
}

export function buildHeaders(method, extra) {
  const headers = toHeadersObject(extra);

  if (method && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
  }

  const { token, uid } = getApiAuthInfo();
  if (token) {
    headers['web_token'] = token;
    headers['token'] = token;
    headers['uid'] = uid;
  }
  // 海外环境添加海外相关的请求头
  if (isOverseasEnv()) {
    Object.assign(headers, getOverseaHeader());
  }
  const cooperationInfo = storage.get(LOCAL_STORAGE_KEY.COOPERATION, true);

  headers['os'] = isElectron ? 'pc-app' : 'brm';
  headers['c'] = 'pc';
  headers['webenv'] = 'comein';
  headers['uc'] = cooperationInfo?.userChannel || 'comein-pc';
  headers['browse'] = window.navigator.appName;
  headers['ua'] = window.navigator.userAgent;
  headers['b'] = isElectron ? '4.0.0800' : PORTVERSION;

  // Multi-agent: scope API calls to selected workspace (CoPaw console parity)
  try {
    const agentStorage = sessionStorage.getItem('copaw-agent-storage');
    if (agentStorage) {
      const parsed = JSON.parse(agentStorage);
      const selectedAgent = parsed?.state?.selectedAgent;
      if (selectedAgent) {
        headers['X-Agent-Id'] = selectedAgent;
      }
    }
  } catch {
    // ignore sessionStorage / JSON errors
  }

  return headers;
}

// 与 jmHttp 的 axios.defaults.timeout 对齐，避免两套请求通道的容错能力不一致
const DEFAULT_TIMEOUT = 30000;

/**
 * 带超时的 fetch。openClaw 与主站不同域，服务异常时连接可能长时间挂起且不报错，
 * 而原生 fetch 没有超时，会让调用方的 Promise 永远 pending（例如列表页 loading 无法结束）。
 * 超时只覆盖「响应头返回前」的等待：拿到响应后立即清除定时器，
 * 因此大文件下载（rawResponse）与流式响应体读取不会被中途 abort。
 * @param {string} url - 完整请求地址
 * @param {object} fetchOptions - 透传给 fetch 的配置
 * @param {number} timeout - 超时毫秒数，传 0 表示不限制
 * @returns {Promise<Response>} fetch 的响应对象
 */
async function fetchWithTimeout(url, fetchOptions, timeout) {
  // 显式关闭超时（0 / 负数）时保持原生 fetch 行为，便于个别长耗时接口按需退化
  if (!timeout || timeout <= 0) {
    return await fetch(url, fetchOptions);
  }

  const controller = new AbortController();
  const externalSignal = fetchOptions.signal;
  const abortByExternal = () => controller.abort();
  // 调用方自带 signal 时需同步到内部 controller，否则其主动取消能力会被覆盖
  if (externalSignal) {
    // 传入时已是 aborted 态：不会再触发 abort 事件，只能立即同步取消
    if (externalSignal.aborted) {
      abortByExternal();
    } else {
      // 尚未取消：挂一次性监听，等调用方取消时再同步到内部 controller
      externalSignal.addEventListener('abort', abortByExternal, { once: true });
    }
  }

  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeout);

  try {
    return await fetch(url, { ...fetchOptions, signal: controller.signal });
  } catch (error) {
    // 区分「超时」与「调用方主动取消」，否则两者都只是一个笼统的 AbortError，线上无法排查
    if (timedOut) {
      throw new Error(`Request timeout after ${timeout}ms: ${url}`);
    }
    throw error;
  } finally {
    clearTimeout(timer);
    externalSignal?.removeEventListener('abort', abortByExternal);
  }
}

export async function request(path, options = {}) {
  const url = getApiUrl(path);
  const method = options.method || 'GET';
  const headers = buildHeaders(method, options.headers);
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;
  const response = await fetchWithTimeout(url, { ...fetchOptions, headers }, timeout);

  // If the response is raw, return it directly
  if (options.rawResponse) {
    return response;
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Request failed: ${response.status} ${response.statusText}${text ? ` - ${text}` : ''}`);
  }

  if (response.status === 204) {
    return undefined;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return await response.text();
  }

  return await response.json();
}

/**
 * POST multipart/form-data (e.g. skill pool ZIP). Omits Content-Type so the browser sets the boundary.
 * @param {string} path - API path
 * @param {File} file - file to upload
 * @param {object} [queryOptions] - optional query: overwrite, target_name, rename_map (object)
 */
export async function postMultipart(path, file, queryOptions = {}) {
  const params = new URLSearchParams();
  if (queryOptions.enable !== undefined) {
    params.set('enable', String(queryOptions.enable));
  }
  if (queryOptions.overwrite !== undefined) {
    params.set('overwrite', String(queryOptions.overwrite));
  }
  if (queryOptions.target_name) {
    params.set('target_name', queryOptions.target_name);
  }
  if (queryOptions.rename_map && Object.keys(queryOptions.rename_map).length) {
    params.set('rename_map', JSON.stringify(queryOptions.rename_map));
  }
  const qs = params.toString();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = getApiUrl(`${normalizedPath}${qs ? `?${qs}` : ''}`);
  const headers = buildHeaders('POST', {});
  delete headers['Content-Type'];

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Request failed: ${response.status} ${response.statusText}${text ? ` - ${text}` : ''}`);
  }

  return await response.json();
}
